import type {
  EeatAnalysisData,
  EeatDetailsData,
  EeatDimensionItem,
  EeatDimensionKey,
  SeoPriorityTone
} from '@/app/ContentTools/types/aiContentAnalysisApiTypes';
import { getScoreGaugeVerdict } from '@/app/ContentTools/components/ai-content-analysis/AiLikenessGauge';

const DIMENSION_META: Record<
  EeatDimensionKey,
  { label: string; order: number }
> = {
  experience: { label: 'تجربه', order: 0 },
  expertise: { label: 'تخصص', order: 1 },
  authoritativeness: { label: 'اعتبار', order: 2 },
  trustworthiness: { label: 'اعتماد', order: 3 }
};

const LEVEL_FA: Record<string, string> = {
  strong: 'قوی',
  moderate: 'متوسط',
  weak: 'ضعیف'
};

export const getEeatLevelTone = (score: number): SeoPriorityTone => {
  if (score >= 75) return 'success';
  if (score >= 50) return 'info';
  if (score >= 25) return 'warning';
  return 'danger';
};

export const getEeatDimensionLevel = (score: number) => {
  if (score >= 75) return 'قوی';
  if (score >= 50) return 'متوسط';
  if (score >= 25) return 'ضعیف';
  return 'بسیار ضعیف';
};

const normalizeLevel = (level: string | undefined, score: number) => {
  if (!level) return getEeatDimensionLevel(score);
  return LEVEL_FA[level.trim().toLowerCase()] || level;
};

const mapEeatData = (data?: EeatDetailsData | null): EeatAnalysisData | null => {
  if (!data) return null;

  const report = data.eeat_report;
  const message = data.eeat?.trim() || '';
  const hasReport =
    report != null &&
    (report.overall_score != null ||
      (report.dimensions && Object.keys(report.dimensions).length > 0) ||
      (report.top_strengths && report.top_strengths.length > 0) ||
      (report.top_gaps && report.top_gaps.length > 0));

  if (!message && !hasReport) return null;

  const dimensions: EeatDimensionItem[] = (
    Object.keys(DIMENSION_META) as EeatDimensionKey[]
  )
    .map(key => {
      const item = report?.dimensions?.[key];
      if (!item || typeof item.score !== 'number') return null;
      return {
        key,
        label: DIMENSION_META[key].label,
        score: item.score,
        level: normalizeLevel(item.level, item.score),
        tone: getEeatLevelTone(item.score)
      };
    })
    .filter((item): item is EeatDimensionItem => item != null)
    .sort(
      (a, b) => DIMENSION_META[a.key].order - DIMENSION_META[b.key].order
    );

  const overallScore =
    typeof report?.overall_score === 'number'
      ? report.overall_score
      : dimensions.length > 0
        ? Math.round(
            dimensions.reduce((sum, item) => sum + item.score, 0) /
              dimensions.length
          )
        : 0;

  return {
    overall_score: overallScore,
    verdict: getScoreGaugeVerdict(overallScore),
    dimensions,
    top_strengths: report?.top_strengths || [],
    top_gaps: report?.top_gaps || [],
    message,
    status: data.eeat_status
  };
};

export default mapEeatData;
